import React, { useRef } from "react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { useEnquiryForm } from "@/context/FormContext";
import { IMAGES } from "@/data";
import { ArrowRight } from "lucide-react";

const LINES = ["CAPITAL FOR AI.", "EXECUTION FOR GROWTH."];

const journey = ["Neural network", "Software", "Customer acquisition", "Robotics", "Manufacturing", "Distribution", "Scaled business"];

export default function Hero() {
  const { openForm } = useEnquiryForm();
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section id="top" ref={ref} className="relative min-h-screen flex items-center overflow-hidden" data-testid="hero-section">
      {/* Parallax background */}
      <motion.div style={reduce ? {} : { y }} className="absolute inset-0 z-0">
        <img src={IMAGES.neural} alt="Abstract neural network representing AI intelligence" className={`w-full h-full object-cover ${reduce ? "opacity-40" : "opacity-30"}`} loading="eager" decoding="async" fetchPriority="high" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#050507] via-[#050507]/70 to-[#050507]" />
        <div className="absolute inset-0 grid-lines opacity-40" />
      </motion.div>
      <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[900px] glow-blue z-0" />

      <motion.div style={reduce ? {} : { opacity }} className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pt-28 pb-20 w-full">
        <motion.div initial={reduce ? false : { opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8 }} className="mb-8 flex items-center gap-3">
          <span className="h-px w-10 bg-[#0057FF]" />
          <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#00D1FF]">AI Venture Development &amp; Commercialisation Network</span>
        </motion.div>

        <h1 className="font-head font-light tracking-tight leading-[0.95] text-5xl md:text-7xl lg:text-[5.5rem]">
          {LINES.map((line, li) => (
            <span key={li} className="block mask-line">
              <motion.span
                className="block text-metallic"
                initial={reduce ? false : { y: "110%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 1.2, delay: 0.2 + li * 0.15, ease: [0.16, 1, 0.3, 1] }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p initial={reduce ? false : { opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.8 }} className="mt-8 max-w-2xl text-base md:text-lg font-light text-zinc-400 leading-relaxed">
          FundAI helps AI-powered businesses prepare for capital, build commercially viable products, enter markets and scale — from software and marketing to robotics and manufacturing.
        </motion.p>
        <motion.p initial={reduce ? false : { opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85, duration: 0.8 }} className="mt-4 max-w-2xl text-sm text-zinc-500 leading-relaxed">
          Founders can apply for capital-readiness and commercialisation support, eligible clients can request consultations about approved financial products, and strategic organisations can join an execution-focused AI ecosystem.
        </motion.p>

        <motion.div initial={reduce ? false : { opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1, duration: 0.8 }} className="mt-10 flex flex-col sm:flex-row gap-3">
          <button onClick={() => openForm("founder")} className="group inline-flex items-center justify-center gap-2 bg-[#0057FF] hover:bg-[#0047D6] text-white rounded-full px-7 py-3.5 font-medium transition-colors active:scale-[0.98]" data-testid="hero-apply-btn">
            Apply for Funding <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
          </button>
          <button onClick={() => openForm("investment")} className="inline-flex items-center justify-center gap-2 border border-white/15 hover:border-white/40 text-white rounded-full px-7 py-3.5 font-medium transition-colors" data-testid="hero-investment-btn">
            Explore Investment Access
          </button>
          <button onClick={() => openForm("partner")} className="inline-flex items-center justify-center gap-2 text-zinc-300 hover:text-white rounded-full px-5 py-3.5 font-medium transition-colors" data-testid="hero-partner-btn">
            Become a Strategic Partner
          </button>
        </motion.div>

        <motion.p initial={reduce ? false : { opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.2 }} className="mt-6 max-w-2xl text-xs text-zinc-600 leading-relaxed">
          Applications and enquiries are subject to eligibility, evaluation, due diligence, documentation, provider acceptance and applicable law. Submission does not guarantee funding, investment access or commercial engagement.
        </motion.p>

        {/* Journey strip */}
        <motion.div initial={reduce ? false : { opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.4 }} className="mt-16 flex flex-wrap items-center gap-x-2 gap-y-2 font-mono text-[11px] uppercase tracking-widest text-zinc-600">
          {journey.map((j, i) => (
            <React.Fragment key={j}>
              <span className="text-zinc-400">{j}</span>
              {i < journey.length - 1 && <span className="text-[#0057FF]">→</span>}
            </React.Fragment>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
}
